import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  FileText, Download, FileSpreadsheet, Eye, Loader2,
} from 'lucide-react';
import { JURISDICTION_NAMES } from '../../lib/constants';
import {
  JURISDICTION_FLAGS, formatDate, getScoreColor, getScoreLevel,
} from './adminUtils';

export const GuestAssessmentsTab = ({
  guestAssessments,
  onView,
  onDownloadWord,
  onDownloadExcel,
  downloadingWord,
  downloadingExcel,
}) => {
  const completed = guestAssessments.filter((a) => a.status === 'completed');
  const inProgress = guestAssessments.filter((a) => a.status !== 'completed');

  return (
    <div className="space-y-6" data-testid="admin-guest-assessments-tab">
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-6">
          <h3 className="text-white font-semibold mb-1 flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#10B981]" />
            Completed Guest Assessments ({completed.length})
          </h3>
          <p className="text-slate-500 text-sm mb-4">
            Full results, Word report, and Excel Q&amp;A download for every guest submission.
          </p>

          {completed.length === 0 ? (
            <p className="text-slate-500 text-center py-8">
              No completed guest assessments yet. Send invitations from the Invitations tab to get started.
            </p>
          ) : (
            <div className="space-y-3">
              {completed.map((assessment) => {
                const guest = assessment.guest_info || {};
                const score = assessment.internal_scores?.overall_score || 0;
                const rating = assessment.internal_scores?.overall_rating?.rating || 'N/A';
                const isDownloadingWord = downloadingWord === assessment.id;
                const isDownloadingExcel = downloadingExcel === assessment.id;
                return (
                  <div
                    key={assessment.id}
                    className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-slate-700/30 rounded-lg gap-4"
                    data-testid={`guest-row-${assessment.id}`}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <span className="text-2xl">{JURISDICTION_FLAGS[assessment.jurisdiction]}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-medium truncate">{guest.name || 'Unknown'}</p>
                        <p className="text-slate-500 text-sm truncate">
                          {guest.company_name || 'No company'} • {guest.email}
                        </p>
                        <p className="text-slate-600 text-xs">
                          {JURISDICTION_NAMES[assessment.jurisdiction]} • Completed {formatDate(assessment.completed_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={getScoreColor(getScoreLevel(score))}>
                        {score.toFixed(0)}%
                      </Badge>
                      <Badge className="bg-slate-600 text-slate-300">{rating}</Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`view-guest-${assessment.id}`}
                        onClick={() => onView(assessment)}
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        title="View full results"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`download-word-${assessment.id}`}
                        onClick={() => onDownloadWord(assessment)}
                        disabled={isDownloadingWord}
                        className="border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10"
                        title="Download Word report"
                      >
                        {isDownloadingWord ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4 mr-1" />
                        )}
                        Word
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`download-excel-${assessment.id}`}
                        onClick={() => onDownloadExcel(assessment)}
                        disabled={isDownloadingExcel}
                        className="border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10"
                        title="Download Excel Q&A"
                      >
                        {isDownloadingExcel ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <FileSpreadsheet className="h-4 w-4 mr-1" />
                        )}
                        Excel
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {inProgress.length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#F59E0B]" />
              In-Progress Guest Assessments ({inProgress.length})
            </h3>
            <div className="space-y-3">
              {inProgress.map((assessment) => {
                const guest = assessment.guest_info || {};
                return (
                  <div
                    key={assessment.id}
                    className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-2xl">{JURISDICTION_FLAGS[assessment.jurisdiction]}</span>
                      <div>
                        <p className="text-white font-medium">{guest.name || 'Unknown'}</p>
                        <p className="text-slate-500 text-sm">
                          {guest.company_name} • {guest.email}
                        </p>
                        <p className="text-slate-600 text-xs">
                          Started {formatDate(assessment.created_at)} • Last saved {formatDate(assessment.last_saved_at)}
                        </p>
                      </div>
                    </div>
                    <Badge className="bg-[#F59E0B]/10 text-[#F59E0B]">In Progress</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
